import React from "react";

import NumberInput from "../../../components/NumberInput";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import xmarkIcon from "../../../assets/xmark.svg";
import {
  MODBUS_REGISTER_TYPES,
  MODBUS_TABLES,
  mappingEndAddress,
  mappingWidth,
} from "./plc-protocol-servers-model";

export const FieldError = ({ children }) =>
  children ? <div className="plcProtocolFieldError">{children}</div> : null;

export const NumberField = ({ label, hint, error, disabled, ...props }) => (
  <div className="gwField">
    <label className="gwLabel">{label}</label>
    <NumberInput {...props} disabled={disabled} aria-invalid={!!error} />
    {hint ? <div className="gwHint">{hint}</div> : null}
    <FieldError>{error}</FieldError>
  </div>
);

export const errorMessage = (error, fallback) =>
  String(error?.message || error || "").trim() || fallback;

export const MappingEditor = ({
  mapping,
  index,
  error,
  disabled,
  tagListId = "plc-protocol-tag-paths",
  tagInputMode = "picker",
  tagStatus,
  onChange,
  onRemove,
}) => {
  const table = MODBUS_TABLES.find((item) => item.value === mapping.table);
  const registerMapping = !["coils", "discrete_inputs"].includes(mapping.table);
  const endAddress = mappingEndAddress(mapping);
  const width = mappingWidth(mapping);
  const set = (field) => (event) => onChange(index, { [field]: event.target.value });
  const setValue = (field) => (value) => onChange(index, { [field]: value });
  const setValueType = (value) => onChange(index, {
    valueType: value,
    ...(value === "string" ? { wordOrder: "big" } : {}),
  });
  const setChecked = (field) => (event) => onChange(index, { [field]: event.target.checked });
  const setTagPath = (_tagID, path) => onChange(index, { tagPath: path });
  const setTable = (nextTable) => {
    const nextIsRegister = !["coils", "discrete_inputs"].includes(nextTable);
    onChange(index, {
      table: nextTable,
      valueType: nextIsRegister ? (mapping.valueType === "bool" ? "uint16" : mapping.valueType) : "bool",
    });
  };

  return (
    <div className="plcProtocolMapping" data-gw-resource="modbus_server_mappings">
      <div className="plcProtocolMappingHeader">
        <div>
          <strong>Mapping {index + 1}</strong>
          <span className={`plcProtocolAccess ${table?.writable ? "isWritable" : ""}`}>
            {table?.writable ? "Read / write" : "Read only"}
          </span>
        </div>
        <button type="button" className="gwIconButton" disabled={disabled} onClick={() => onRemove(index)} aria-label={`Remove mapping ${index + 1}`} title="Remove mapping">
          <img src={xmarkIcon} alt="" className="gwIcon" />
        </button>
      </div>
      <div className="plcProtocolMappingGrid">
        <div className="gwField">
          <label className="gwLabel">Table</label>
          <Select value={mapping.table} options={MODBUS_TABLES} onChange={setTable} disabled={disabled} />
        </div>
        <NumberField
          label="Zero-based address"
          value={mapping.address}
          min={0}
          max={65535}
          step={1}
          onChange={set("address")}
          disabled={disabled}
          hint={Number.isInteger(endAddress) && width > 1 ? `Occupies ${mapping.address}–${endAddress} (${width} registers)` : "Protocol address 0–65535"}
        />
        <div className="gwField plcProtocolTagField">
          <label className="gwLabel">Control Seat tag path</label>
          {tagInputMode === "manual" ? (
            <input className="gwInput" list={tagListId} value={mapping.tagPath} onChange={set("tagPath")} placeholder="default/Line 1/Speed" disabled={disabled} aria-invalid={!!error || tagStatus?.eligible === false} autoComplete="off" />
          ) : (
            <TagPicker
              value=""
              displayValue={mapping.tagPath}
              placeholder="Search or choose a tag…"
              disabled={disabled}
              onChange={setTagPath}
            />
          )}
          <div className="gwHint">Portable full path; tag ownership and quality rules still apply.</div>
          {tagStatus?.message ? (
            <div className={`plcProtocolTagStatus ${tagStatus.eligible ? "isEligible" : "isBlocked"}`}>
              {tagStatus.message}
            </div>
          ) : null}
        </div>
        {registerMapping ? (
          <>
            <div className="gwField">
              <label className="gwLabel">Value type</label>
              <Select value={mapping.valueType} options={MODBUS_REGISTER_TYPES} onChange={setValueType} disabled={disabled} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Byte order</label>
              <Select
                value={mapping.byteOrder}
                options={[{ value: "big", label: "Big endian" }, { value: "little", label: "Little endian" }]}
                onChange={setValue("byteOrder")}
                disabled={disabled}
              />
            </div>
            <div className="gwField">
              <label className="gwLabel">Word order</label>
              <Select
                value={mapping.wordOrder}
                options={[{ value: "big", label: "High word first" }, { value: "little", label: "Low word first" }]}
                onChange={setValue("wordOrder")}
                disabled={disabled || width === 1 || mapping.valueType === "string"}
              />
              {width === 1 ? <div className="gwHint">Not used for one-register values.</div> : null}
            </div>
            {mapping.valueType === "string" ? (
              <>
                <NumberField
                  label="String length (bytes)"
                  value={mapping.stringLength}
                  min={2}
                  max={250}
                  step={2}
                  onChange={set("stringLength")}
                  disabled={disabled}
                  hint="Fixed register capacity; two characters per register."
                />
                <label className="gwCheckboxRow">
                  <input type="checkbox" checked={mapping.stringRaw === true} onChange={setChecked("stringRaw")} disabled={disabled} />
                  <span><strong>Preserve padding bytes</strong><small>Keep trailing zero and space padding in the tag value.</small></span>
                </label>
                <label className="gwCheckboxRow">
                  <input type="checkbox" checked={mapping.stringRightJustify === true} onChange={setChecked("stringRightJustify")} disabled={disabled} />
                  <span><strong>Right-align writes</strong><small>Pad the beginning of shorter strings instead of the end.</small></span>
                </label>
              </>
            ) : null}
          </>
        ) : (
          <div className="gwField">
            <label className="gwLabel">Value type</label>
            <input className="gwInput" value="Boolean" disabled readOnly />
          </div>
        )}
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
};
